// @spec GHG-STR-010
'use client';

/**
 * Activity Data edit page.
 * Displays and allows editing of a single activity data entry.
 */

import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useActivityDataEntry, useDeleteActivityData } from '@/features/ghg/api/use-activity-data';
import { ActivityDataEntryForm } from '@/features/ghg/components';
import { getActivityCalculationMethodLabel } from '@/features/ghg/config/activity-calculation-methods';
import { toDisplayActivityUnit } from '@/features/ghg/lib/unit-conversion';
import type { ActivityDataResponse } from '@/features/ghg/types/activity-data';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/ghg/activity-data/$entryId')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.editActivityData'),
  }),
  component: ActivityDataEditPage,
});

const STATUS_BADGE_VARIANTS: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  draft: 'secondary',
  submitted: 'outline',
  pending_approval: 'default',
  approved: 'default',
  rejected: 'destructive',
};

function toTitleCaseLabel(raw: string): string {
  return raw
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getActivityLabel(entry: ActivityDataResponse, t: (key: string) => string): string {
  if (entry.activityGroup?.name) return entry.activityGroup.name;
  if (entry.activityDisplayName?.trim()) return entry.activityDisplayName;
  if (entry.value.activityType.startsWith('catalog:')) return t('activityDataDetail.catalogActivity');
  if (entry.value.activityType.trim()) return toTitleCaseLabel(entry.value.activityType);
  return toTitleCaseLabel(entry.value.activityCategory);
}

function getStatusLabel(status: string, t: (key: string) => string): string {
  const labels: Record<string, string> = {
    draft: t('activityDataDetail.statusDraft'),
    submitted: t('activityDataDetail.statusSubmitted'),
    pending_approval: t('activityDataDetail.statusPendingApproval'),
    approved: t('activityDataDetail.statusApproved'),
    rejected: t('activityDataDetail.statusRejected'),
  };
  return labels[status] ?? status;
}

function formatEntryEmissions(estimatedCo2eKg: number | null | undefined, t: (key: string) => string): string {
  if (estimatedCo2eKg == null) return t('activityDataDetail.notAvailable');
  if (estimatedCo2eKg >= 1000) {
    return `${(estimatedCo2eKg / 1000).toLocaleString(undefined, {
      maximumFractionDigits: 4,
    })} tCO2e`;
  }
  return `${estimatedCo2eKg.toLocaleString(undefined, { maximumFractionDigits: 4 })} kg CO2e`;
}

function resolveFactorDisplayUnit(entry: ActivityDataResponse): string {
  if (entry.value.emissionFactorActivityUnit) {
    return entry.value.emissionFactorActivityUnit;
  }
  if (entry.value.emissionFactorRawUnit) {
    return toDisplayActivityUnit(entry.value.emissionFactorRawUnit);
  }
  return entry.value.unit;
}

function formatScope2Method(method: string | null | undefined, t: (key: string) => string): string {
  if (!method) return '-';
  if (method === 'location_based') return t('activityDataDetail.locationBased');
  if (method === 'market_based') return t('activityDataDetail.marketBased');
  if (method === 'both') return t('activityDataDetail.both');
  return method;
}

function formatBooleanFlag(value: boolean | null | undefined, t: (key: string) => string): string {
  return value ? t('activityDataDetail.yes') : t('activityDataDetail.no');
}

interface SoldProductMarketSplitRow {
  region: string;
  sharePercent: number;
}

interface SoldProductProvenance {
  selectedMarketRegion: string | null;
  selectedMarketSharePercent: number | null;
  marketSplit: SoldProductMarketSplitRow[];
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function parseSoldProductProvenance(entry: ActivityDataResponse): SoldProductProvenance | null {
  const parameters = asRecord(entry.value.emissionFactorParameters);
  if (!parameters || parameters.matcher !== 'sold_product_ghg') return null;

  const soldProductContext = asRecord(parameters.soldProductContext);
  if (!soldProductContext) return null;

  const rawMarketSplit = Array.isArray(soldProductContext.marketSplit)
    ? soldProductContext.marketSplit
    : [];
  const marketSplit = rawMarketSplit.flatMap((item) => {
    const record = asRecord(item);
    const region = typeof record?.region === 'string' ? record.region.trim() : '';
    const sharePercentRaw = record?.sharePercent;
    const sharePercent =
      typeof sharePercentRaw === 'number'
        ? sharePercentRaw
        : typeof sharePercentRaw === 'string'
          ? Number(sharePercentRaw)
          : Number.NaN;
    if (!region || !Number.isFinite(sharePercent)) return [];
    return [{ region, sharePercent }];
  });

  const selectedMarketRegion =
    typeof soldProductContext.selectedMarketRegion === 'string' &&
    soldProductContext.selectedMarketRegion.trim()
      ? soldProductContext.selectedMarketRegion.trim()
      : null;
  const selectedShareRaw = soldProductContext.selectedMarketSharePercent;
  const selectedMarketSharePercent =
    typeof selectedShareRaw === 'number'
      ? selectedShareRaw
      : typeof selectedShareRaw === 'string'
        ? Number(selectedShareRaw)
        : Number.NaN;

  return {
    selectedMarketRegion,
    selectedMarketSharePercent: Number.isFinite(selectedMarketSharePercent)
      ? selectedMarketSharePercent
      : null,
    marketSplit,
  };
}

function getScope2MarketInputMode(entry: ActivityDataResponse, t: (key: string) => string): string {
  if (
    typeof entry.value.scope2MarketEmissionsKg === 'number' ||
    typeof entry.value.scope2Context?.marketEmissionsKg === 'number'
  ) {
    return t('activityDataDetail.manualMarketEmissionsTotal');
  }
  if (
    entry.value.scope2MarketFactor ||
    typeof entry.value.scope2Context?.marketFactorKgCo2ePerKwh === 'number'
  ) {
    return t('activityDataDetail.separateMarketBasedFactor');
  }
  return t('activityDataDetail.sameAsLocationBased');
}

/**
 * Activity Data Edit page.
 *
 * Allows users to:
 * - View activity data entry details
 * - Edit non-approved entries
 * - Delete non-approved entries
 * - Navigate back to the list
 */
function ActivityDataEditPage() {
  const { t } = useTranslation('ghg');
  const navigate = useNavigate();
  const { entryId } = Route.useParams();
  const { membership, organization } = useOrganization();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Get internal org ID from Clerk organization metadata
  const orgId = (organization?.publicMetadata as { internalId?: string } | undefined)?.internalId;

  // Fetch the activity data entry
  const { entry, isLoading, isError, error } = useActivityDataEntry(entryId);
  const errorMessage = error instanceof Error ? error.message : t('activityDataDetail.unknownError');

  // Delete mutation
  const deleteMutation = useDeleteActivityData();

  const isApproved = entry?.status === 'approved';
  const isScope1Entry = entry?.value.scope === 1;
  const isReadOnly = isApproved;
  const canDelete = entry && !isReadOnly;

  // Handle back navigation
  const handleBack = () => {
    void navigate({ to: '/ghg/activity-data' });
  };

  // Handle form success
  const handleFormSuccess = (_entry: ActivityDataResponse) => {
    toast.success(t('toast.entryUpdated'), {
      description: t('activityDataDetail.toastEntryUpdatedDesc'),
    });
    handleBack();
  };

  // Handle cancel
  const handleCancel = () => {
    handleBack();
  };

  // Handle delete
  const handleDelete = async () => {
    if (!entry) return;

    try {
      await deleteMutation.mutateAsync(entry.id);
      toast.success(t('toast.entryDeleted'), {
        description: t('activityDataDetail.toastEntryDeletedDesc'),
      });
      handleBack();
    } catch (err) {
      toast.error(t('toast.deleteFailed'), {
        description: err instanceof Error ? err.message : t('activityDataDetail.failedToDeleteEntry'),
      });
    }
  };

  // Show skeleton while loading
  if (!membership || !orgId || isLoading) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-3xl space-y-6">
          <div className="flex items-center gap-4">
            <Skeleton className="h-10 w-10" />
            <Skeleton className="h-9 w-64" />
          </div>
          <Skeleton className="h-[600px] w-full" />
        </div>
      </div>
    );
  }

  // Show error state
  if (isError) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-3xl">
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" size="icon" onClick={handleBack}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-semibold">{t('activityDataDetail.heading')}</h1>
          </div>
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
            <p className="text-destructive">{t('activityDataDetail.failedToLoadEntry', { error: errorMessage })}</p>
            <Button variant="outline" onClick={handleBack} className="mt-4">
              {t('activityDataDetail.backToList')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show not found state
  if (!entry) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-3xl">
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" size="icon" onClick={handleBack}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-semibold">{t('activityDataDetail.heading')}</h1>
          </div>
          <div className="rounded-lg border border-dashed p-12 text-center">
            <p className="text-muted-foreground">{t('activityDataDetail.entryNotFound')}</p>
            <Button variant="outline" onClick={handleBack} className="mt-4">
              {t('activityDataDetail.backToList')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const scope2CountryDisplayRaw = entry.value.scope2Country ?? entry.value.scope2Context?.country;
  const scope2RegionDisplayRaw =
    entry.value.scope2RegionCode ??
    entry.value.scope2Context?.regionCode ??
    entry.value.scope2Context?.gridRegion;
  const scope2CountryDisplay = scope2CountryDisplayRaw?.trim() ? scope2CountryDisplayRaw : '-';
  const scope2RegionDisplay = scope2RegionDisplayRaw?.trim() ? scope2RegionDisplayRaw : '-';
  const soldProductProvenance = parseSoldProductProvenance(entry);

  return (
    <div className="p-8">
      <div className="mx-auto max-w-3xl space-y-6">
        {/* Header with Back Button and Title */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={handleBack}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-semibold">
                  {isReadOnly ? t('activityDataDetail.viewActivityData') : t('activityDataDetail.editActivityData')}
                </h1>
                <Badge variant={STATUS_BADGE_VARIANTS[entry.status] ?? 'secondary'}>
                  {getStatusLabel(entry.status, t)}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                {getActivityLabel(entry, t)}
                {entry.facilityId ? ` • ${t('activityDataDetail.facilityLinkedActivity')}` : ''}
              </p>
            </div>
          </div>

          {/* Delete Button */}
          {canDelete && (
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" className="gap-2">
                  <Trash2 className="h-4 w-4" />
                  {t('activityDataDetail.delete')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('activityDataDetail.deleteDialogTitle')}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('activityDataDetail.deleteDialogDescription')}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('activityDataDetail.cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => {
                      void handleDelete();
                    }}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {t('activityDataDetail.delete')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>

        {/* Entry Form */}
        <Card>
          <CardHeader>
            <CardTitle>{t('activityDataDetail.entryDetails')}</CardTitle>
            <CardDescription>
              {isReadOnly
                ? t('activityDataDetail.readOnlyDescription')
                : t('activityDataDetail.editDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ActivityDataEntryForm
              mode="edit"
              existingEntry={entry}
              readOnly={isReadOnly}
              onSuccess={handleFormSuccess}
              onCancel={handleCancel}
            />
          </CardContent>
        </Card>

        {!isScope1Entry && (
          <Card>
            <CardHeader>
              <CardTitle>{t('activityDataDetail.calculationSnapshot')}</CardTitle>
              <CardDescription>
                {t('activityDataDetail.calculationSnapshotDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between border-b py-1">
                <span className="text-muted-foreground">{t('activityDataDetail.activity')}</span>
                <span>{getActivityLabel(entry, t)}</span>
              </div>
              <div className="flex justify-between border-b py-1">
                <span className="text-muted-foreground">{t('activityDataDetail.source')}</span>
                <span>{entry.value.emissionFactorSource ?? '-'}</span>
              </div>
              {entry.value.scope !== 2 && (
                <div className="flex justify-between border-b py-1">
                  <span className="text-muted-foreground">{t('activityDataDetail.calculationMethod')}</span>
                  <span>
                    {entry.value.calculationMethod
                      ? getActivityCalculationMethodLabel(entry.value.calculationMethod)
                      : '-'}
                  </span>
                </div>
              )}
              {entry.value.scope === 2 && (
                <>
                  <div className="flex justify-between border-b py-1">
                    <span className="text-muted-foreground">{t('activityDataDetail.locationFactor')}</span>
                    <span>
                      {entry.value.scope2LocationFactor?.emissionFactorName ??
                        (entry.value.scope2LocationFactor?.emissionFactorId
                          ? t('activityDataDetail.manualFactorOverride')
                          : t('activityDataDetail.autoResolvedFactor'))}
                    </span>
                  </div>
                  <div className="flex justify-between border-b py-1">
                    <span className="text-muted-foreground">{t('activityDataDetail.marketInput')}</span>
                    <span>{getScope2MarketInputMode(entry, t)}</span>
                  </div>
                  <div className="flex justify-between border-b py-1">
                    <span className="text-muted-foreground">{t('activityDataDetail.accountingMethod')}</span>
                    <span>
                      {formatScope2Method(
                        entry.value.scope2AccountingMethod ??
                          entry.value.scope2Context?.accountingMethod,
                        t
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between border-b py-1">
                    <span className="text-muted-foreground">{t('activityDataDetail.countryRegion')}</span>
                    <span>
                      {scope2CountryDisplay} / {scope2RegionDisplay}
                    </span>
                  </div>
                  <div className="flex justify-between border-b py-1">
                    <span className="text-muted-foreground">{t('activityDataDetail.locationBasedEmissions')}</span>
                    <span className="font-mono">
                      {formatEntryEmissions(entry.value.scope2LocationCo2eKg, t)}
                    </span>
                  </div>
                  <div className="flex justify-between border-b py-1">
                    <span className="text-muted-foreground">{t('activityDataDetail.marketBasedEmissions')}</span>
                    <span className="font-mono">
                      {formatEntryEmissions(entry.value.scope2MarketCo2eKg, t)}
                    </span>
                  </div>
                  <div className="flex justify-between border-b py-1">
                    <span className="text-muted-foreground">{t('activityDataDetail.primaryDisplayMethod')}</span>
                    <span>{formatScope2Method(entry.value.scope2PrimaryMethod, t)}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between border-b py-1">
                <span className="text-muted-foreground">{t('activityDataDetail.estimatedEmissions')}</span>
                <span className="font-mono">
                  {formatEntryEmissions(entry.value.estimatedCo2eKg, t)}
                </span>
              </div>
              <div className="flex justify-between border-b py-1">
                <span className="text-muted-foreground">{t('activityDataDetail.dataAccuracy')}</span>
                <span>
                  {typeof entry.value.dataAccuracyScore === 'number'
                    ? `${String(entry.value.dataAccuracyScore)} / 10`
                    : '-'}
                </span>
              </div>
              <div className="flex justify-between border-b py-1">
                <span className="text-muted-foreground">{t('activityDataDetail.estimatedData')}</span>
                <span>{formatBooleanFlag(entry.value.dataIsEstimated, t)}</span>
              </div>
              <div className="space-y-1 border-b py-2">
                <div className="text-muted-foreground">{t('activityDataDetail.dataAssumptions')}</div>
                <div className="whitespace-pre-wrap break-words">
                  {entry.value.dataAssumptions?.trim() ?? '-'}
                </div>
              </div>
              {entry.value.scope !== 2 && (
                <>
                  {soldProductProvenance && (
                    <div className="space-y-2 border-b py-2">
                      <div className="text-muted-foreground">{t('activityDataDetail.soldProductMarketProvenance')}</div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('activityDataDetail.selectedMarket')}</span>
                        <span>
                          {soldProductProvenance.selectedMarketRegion ?? '-'}
                          {soldProductProvenance.selectedMarketSharePercent != null
                            ? ` (${soldProductProvenance.selectedMarketSharePercent.toLocaleString(
                                undefined,
                                {
                                  maximumFractionDigits: 2,
                                }
                              )}%)`
                            : ''}
                        </span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-muted-foreground">{t('activityDataDetail.marketSplit')}</span>
                        <span className="text-right">
                          {soldProductProvenance.marketSplit.length > 0
                            ? soldProductProvenance.marketSplit
                                .map(
                                  (market) =>
                                    `${market.region} (${market.sharePercent.toLocaleString(
                                      undefined,
                                      {
                                        maximumFractionDigits: 2,
                                      }
                                    )}%)`
                                )
                                .join(', ')
                            : '-'}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="flex justify-between border-b py-1">
                    <span className="text-muted-foreground">{t('activityDataDetail.factorValue')}</span>
                    <span className="font-mono">
                      {entry.value.emissionFactorValue != null
                        ? `${entry.value.emissionFactorValue.toFixed(6)} kg CO2e/${resolveFactorDisplayUnit(entry)}`
                        : '-'}
                    </span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-muted-foreground">{t('activityDataDetail.normalizedQuantity')}</span>
                    <span className="font-mono">
                      {entry.value.quantityInFactorUnit != null
                        ? `${entry.value.quantityInFactorUnit.toLocaleString(undefined, {
                            maximumFractionDigits: 6,
                          })} ${resolveFactorDisplayUnit(entry)}`
                        : '-'}
                    </span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
